import { get } from 'lodash';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { TextField } from 'Containers/App/types';
import { ClaimsSummaryState, ClaimsSummary, NotFoundError, TimeoutError } from 'Containers/ProfilePage/types';
import { ContentfulInfoBar } from 'ContentfulWrappers/ContentfulInfoBar';
import { useTextContext } from 'Contexts/textContext';
import InfoBar, { InfoBarButton } from 'DesignLibrary/molecules/InfoBar';
import { InfoBarLoader } from 'DesignLibrary/molecules/InfoBarLoader';
import RichText from 'Shared/RichText';
import { toDollars } from 'Utils/helpers';
import { PROFILE_PATH } from 'Utils/urls';

export interface ClaimsSummaryBarProps {
  claimsSummary: ClaimsSummary | null;
  claimsSummaryState: ClaimsSummaryState;
  claimsError: Error | null;
  field: TextField;
  handleCloseClaimsContent: () => void;
  handleRetryClaims: () => void;
  fieldDataComplete?: boolean;
}

export const ClaimsSummaryBar = ({
  field,
  claimsSummary,
  claimsError,
  claimsSummaryState,
  handleCloseClaimsContent,
  handleRetryClaims,
  fieldDataComplete = true,
}: ClaimsSummaryBarProps) => {
  const successVars = useMemo(() => {
    const { oopTowardMaxDollars, maxDollars, oopTowardDeductibleDollars, deductibleDollars } =
      claimsSummary || {};
    return {
      oopSpent: toDollars(oopTowardMaxDollars ?? 0, 2),
      oopMax: toDollars(maxDollars ?? 0, 0),
      deductibleSpent:
        typeof oopTowardDeductibleDollars === 'number' ? toDollars(oopTowardDeductibleDollars ?? 0, 2) : null,
      deductibleTotal: typeof deductibleDollars === 'number' ? toDollars(deductibleDollars ?? 0, 0) : null,
    };
  }, [claimsSummary]);

  const InfoBar =
    claimsSummaryState === ClaimsSummaryState.Error ? (
      <ErrorInfoBar
        error={claimsError}
        handleCloseClaimsContent={handleCloseClaimsContent}
        handleRetryClaims={handleRetryClaims}
      />
    ) : (
      <SuccessInfoBar field={field} vars={successVars} />
    );

  const isLoading = claimsSummaryState === ClaimsSummaryState.Loading;

  // hide bar if data is loaded but incomplete for the given section
  if (claimsSummaryState === ClaimsSummaryState.Loaded && !fieldDataComplete) {
    return null;
  }

  if (claimsSummaryState === ClaimsSummaryState.NotAvailable) {
    return null;
  }

  return (
    <div data-testid="claims-summary-bar">
      <InfoBarLoader isLoading={isLoading} field="claims_summary_bar.loading_message">
        {InfoBar}
      </InfoBarLoader>
    </div>
  );
};

const SuccessInfoBar = ({ vars, field }) => {
  return <ContentfulInfoBar field={field} vars={vars} />;
};

type ErrorInfoBarProps = {
  error: Error | null;
  handleCloseClaimsContent: () => void;
  handleRetryClaims: () => void;
  retryUrl?: string;
  navigateToProfilePage?: boolean;
};

export const ErrorInfoBar = ({
  error,
  handleCloseClaimsContent,
  handleRetryClaims,
  retryUrl = '#claims-section',
  navigateToProfilePage = false,
}: ErrorInfoBarProps) => {
  let field: TextField;
  let leftButtonState: InfoBarButton = {};
  let rightButtonState: InfoBarButton = {};

  const { textMap } = useTextContext();
  const navigate = useNavigate();
  const navigateToClaims = () => navigate(PROFILE_PATH);

  if (error === null) {
    throw new Error(`ClaimsSummaryState was ERROR but no error details provided`);
  }

  if (error instanceof NotFoundError) {
    // 502 status code from JVP
    field = 'claims_summary_bar.error_message';
    leftButtonState = {
      text: get(textMap, 'claims_summary_bar.error_continue_button' as string), // No more polling, close this error bar,
      onClick: handleCloseClaimsContent,
    };
    rightButtonState = navigateToProfilePage
      ? {
          text: get(textMap, 'claims_summary_bar.error_retry_button' as string), // This version appears on the results page and navigates the user back to the profile
          onClick: navigateToClaims,
        }
      : {
          text: get(textMap, 'claims_summary_bar.error_retry_button' as string), // Send back to the current medical spending section
          url: retryUrl,
          target: '_self',
        };
  } else if (error instanceof TimeoutError) {
    field = 'claims_summary_bar.timeout_message';
    leftButtonState = {
      text: get(textMap, 'claims_summary_bar.timeout_wait_button' as string), // Retry the polling
      onClick: handleRetryClaims,
    };
    rightButtonState = {
      text: get(textMap, 'claims_summary_bar.timeout_continue_button' as string), // No more polling, close this error bar
      onClick: handleCloseClaimsContent,
    };
  } else {
    throw new Error(`Unhandled errorType: ${error}`);
  }

  return (
    <div>
      <InfoBar
        text={<RichText noWrapper field={field} />}
        color="yellow"
        key={field}
        icon="Warning"
        buttons={[leftButtonState, rightButtonState]}
      />
    </div>
  );
};
